import { cn } from '@/lib/utils';
import { CellValue } from './CellValue';

interface ChangeCardProps {
  changes?: { field: string; from: string | null; to: string | null }[];
  className?: string;
}

/**
 * `shape: 'change'` — the field-diff card (design §1.4 "Field diff").
 *
 * **Every entry stacks inside ONE card.** Screenshot 02 is the reference: DELIVERY ADDRESS and REQUIRED DATE
 * from a single "edited the shipping details" event share one bordered card, because they were one edit. A
 * card per field would read as several edits and inflate a two-field change into a wall.
 *
 * The diff is never reconstructed here — the server sends `from`/`to` already resolved, and an event that
 * carries no before/after simply has no `change` block.
 */
export function ChangeCard({ changes, className }: ChangeCardProps) {
  const safeChanges = Array.isArray(changes) ? changes : [];

  if (safeChanges.length === 0) return null;

  return (
    <div
      className={cn(
        'flex flex-col gap-1.5 rounded-lg border border-border bg-muted/40 px-2.5 py-2',
        className
      )}
    >
      {safeChanges.map((change, index) => (
        <div key={`${change.field}-${index}`} className="min-w-0">
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
            {change.field}
          </div>

          {/* `break-words`, not truncation — a delivery address is the entire point of the row, and at 364px
              it will wrap. */}
          <div className="break-words text-xs leading-normal">
            {change.from ? (
              <s className="text-muted-foreground">{change.from}</s>
            ) : (
              // An absent old value renders as a plain em-dash rather than a struck one: striking a marker
              // that stands for "there was nothing" would read as "nothing was removed".
              <CellValue value={null} />
            )}
          </div>

          <div className="break-words text-xs font-medium leading-normal text-foreground">
            <CellValue value={change.to} />
          </div>
        </div>
      ))}
    </div>
  );
}
